import React, { useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { StickyFooter } from '../components/StickyFooter';
import { FadeIn } from '../components/FadeIn';
import { CalendarIcon, ClockIcon, ArrowRightIcon } from 'lucide-react';
const FEATURED_POST = {
  title: "Navigating Post-Hospital Care: A Complete Guide for Families",
  excerpt: "The first 48 hours after a hospital discharge are critical. Learn how to prepare your home, manage medications, and ensure a smooth recovery with the right support system.",
  category: "Home Care",
  date: "October 12, 2026",
  readTime: "8 min read",
  image: "https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=1200&auto=format&fit=crop",
  author: "Sarah Jenkins, RN"
};
const RECENT_POSTS = [{
  id: 1,
  title: "Top 5 Questions to Ask When Hiring an In-Home Nanny",
  excerpt: "Finding the perfect match for your family goes beyond a background check. Here are the essential questions to ask during your nanny interviews.",
  category: "Childcare",
  date: "October 8, 2026",
  readTime: "5 min read",
  image: "https://images.unsplash.com/photo-1544281679-44463372c544?w=600&auto=format&fit=crop"
}, {
  id: 2,
  title: "Why Hospital-Grade Sanitization Matters for Mobility Rentals",
  excerpt: "Not all rental equipment is treated equally. Discover the rigorous cleaning protocols that keep your family safe when renting wheelchairs and scooters.",
  category: "Equipment",
  date: "October 3, 2026",
  readTime: "4 min read",
  image: "https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?w=600&auto=format&fit=crop"
}, {
  id: 3,
  title: "The Benefits of Flexible Shifts for Healthcare Workers",
  excerpt: "Burnout is a real issue in healthcare. Learn how taking control of your own schedule can improve patient outcomes and your own mental health.",
  category: "Careers",
  date: "September 28, 2026",
  readTime: "6 min read",
  image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&auto=format&fit=crop"
}, {
  id: 4,
  title: "How to Prepare Your Home for an Aging Parent",
  excerpt: "Simple modifications can make a world of difference. A room-by-room guide to creating a safe and accessible environment for seniors.",
  category: "Home Care",
  date: "September 22, 2026",
  readTime: "7 min read",
  image: "https://images.unsplash.com/photo-1516156008625-3a9d6067fab5?w=600&auto=format&fit=crop"
}, {
  id: 5,
  title: "Emergency Backup Childcare: Building Your Safety Net",
  excerpt: "School closures and sick days happen. How to build a reliable backup childcare plan so you're never caught off guard.",
  category: "Childcare",
  date: "September 15, 2026",
  readTime: "5 min read",
  image: "https://images.unsplash.com/photo-1587691592099-24045742c181?w=600&auto=format&fit=crop"
}, {
  id: 6,
  title: "Traveling with Limited Mobility: Essential Gear Guide",
  excerpt: "Don't let mobility challenges stop you from exploring. The best travel-friendly scooters and wheelchairs for your next vacation.",
  category: "Equipment",
  date: "September 10, 2026",
  readTime: "6 min read",
  image: "https://images.unsplash.com/photo-1519689680058-324335c77eba?w=600&auto=format&fit=crop"
}];
const CATEGORIES = ["All", "Home Care", "Childcare", "Equipment", "Careers", "Company News"];
export function Blog() {
  useEffect(() => {
    document.title = "Blog & Resources | Care Nomad";
  }, []);
  return <div className="min-h-screen w-full bg-gray-50 flex flex-col" data-id="element-645" data-mp="31er5hxl3ylwa">
      <Navbar data-id="element-646" data-mp="2ngssaviiwvid" />
      
      <main className="flex-1" data-id="element-647" data-mp="2nf7yx97y3im3">
        {/* Header */}
        <section className="w-full relative py-12 lg:py-16 flex items-center justify-center min-h-[40vh]" data-id="element-648" data-mp="2sfcd2slds195">
          <div className="absolute inset-0 z-0" data-id="element-649" data-mp="1c1dsljyxjpjk">
            <img src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&auto=format&fit=crop" alt="Blog Background" className="w-full h-full object-cover" data-id="element-650" data-mp="1bzjaedf1mbj6" />
            <div className="absolute inset-0 bg-[#1a2332]/80" data-id="element-651" data-mp="1wavted1now93"></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-652" data-mp="1rilva9qe0pvq">
            <FadeIn direction="up" data-id="element-653" data-mp="1mjj1jlfuy632">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4" data-id="element-654" data-mp="1mku2ldhx4zqh">
                Care Nomad <span className="text-[#007a87]" data-id="element-655" data-mp="1ika7sgkti87i">Insights</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed" data-id="element-656" data-mp="2f86g6y63px7n">
                Expert advice, industry news, and helpful resources for families, independent caregivers, and healthcare facilities.
              </p>
            </FadeIn>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-657" data-mp="218f5p7esmyye">
          {/* Categories */}
          <FadeIn direction="up" delay={0.1} data-id="element-658" data-mp="uufwi3g5crpd">
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 mb-12" data-id="element-659" data-mp="pu1lqdn5v3nn">
              {CATEGORIES.map((cat, idx) => <button key={idx} className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${idx === 0 ? 'bg-[#007a87] text-white' : 'bg-white text-[#5a6a7a] hover:bg-gray-100 border border-gray-200'}`} data-id="element-660" data-mp="31ieyk3jcumgt" data-mpi={idx}>
                  {cat}
                </button>)}
            </div>
          </FadeIn>

          {/* Featured Post */}
          <FadeIn direction="up" delay={0.2} data-id="element-661" data-mp="3p5emohdmo41u">
            <div className="mb-16 bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100 group cursor-pointer hover:shadow-xl transition-all duration-300 flex flex-col lg:flex-row" data-id="element-662" data-mp="3p8ivjs21fnyu">
              <div className="lg:w-3/5 h-64 lg:h-auto relative overflow-hidden" data-id="element-663" data-mp="1zno84bymz41">
                <img src={FEATURED_POST.image} alt={FEATURED_POST.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-664" data-mp="1ztumo0snoo5k" />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-[#007a87]" data-id="element-665" data-mp="325pgoqrha7d2">
                  Featured • {FEATURED_POST.category}
                </div>
              </div>
              <div className="lg:w-2/5 p-8 lg:p-12 flex flex-col justify-center" data-id="element-666" data-mp="3c1sj7xq8s80j">
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4 font-medium" data-id="element-667" data-mp="3q23c16b43woc">
                  <span className="flex items-center gap-1.5" data-id="element-668" data-mp="3l30iai0l1cvo"><CalendarIcon className="w-4 h-4" data-id="element-669" data-mp="3l1xnw041wsb0" /> {FEATURED_POST.date}</span>
                  <span className="flex items-center gap-1.5" data-id="element-670" data-mp="3mqz2vxc0yat"><ClockIcon className="w-4 h-4" data-id="element-671" data-mp="3uu3ilhlotntw" /> {FEATURED_POST.readTime}</span>
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold text-[#1a2332] mb-4 group-hover:text-[#007a87] transition-colors" data-id="element-672" data-mp="cnp51jrbzfw1">
                  {FEATURED_POST.title}
                </h2>
                <p className="text-[#5a6a7a] leading-relaxed mb-8 text-lg" data-id="element-673" data-mp="rb70drj06y5s">
                  {FEATURED_POST.excerpt}
                </p>
                <div className="flex items-center justify-between mt-auto" data-id="element-674" data-mp="w8pcfqsbhti0">
                  <div className="flex items-center gap-3" data-id="element-675" data-mp="iasgmwlotr4e">
                    <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden" data-id="element-676" data-mp="iclm48kke5at">
                      <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100&auto=format&fit=crop" alt="Author" className="w-full h-full object-cover" data-id="element-677" data-mp="mdxeumyqj6n8" />
                    </div>
                    <span className="font-bold text-[#1a2332] text-sm" data-id="element-678" data-mp="dch76mzb1imp">{FEATURED_POST.author}</span>
                  </div>
                  <span className="text-[#007a87] font-bold flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-679" data-mp="3lcphfi8vjo6">
                    Read Article <ArrowRightIcon className="w-4 h-4" data-id="element-680" data-mp="3lqpq13c0yv1o" />
                  </span>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Recent Posts Grid */}
          <div className="mb-8" data-id="element-681" data-mp="a2xzc7v1gmiw">
            <h3 className="text-2xl font-bold text-[#1a2332] mb-8" data-id="element-682" data-mp="a265b1h3awuq">Latest Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-683" data-mp="54wfvltpal6w">
              {RECENT_POSTS.map((post, idx) => <FadeIn key={post.id} direction="up" delay={0.1 * (idx % 3)} data-id="element-684" data-mp="6cupnmxldfl" data-mpi={idx}>
                  <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 group cursor-pointer hover:shadow-xl transition-all duration-300 flex flex-col h-full" data-id="element-685" data-mp="3s818q4s1fkp4">
                    <div className="h-48 w-full relative overflow-hidden" data-id="element-686" data-mp="3igwwhctqydxz">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-687" data-mp="3dkg7yhimxxkd" />
                      <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-[#007a87]" data-id="element-688" data-mp="34kjfs5x1egkk">
                        {post.category}
                      </div>
                    </div>
                    <div className="p-6 flex flex-col flex-1" data-id="element-689" data-mp="1jzwcbtiba42n">
                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-3 font-medium" data-id="element-690" data-mp="1o29igj0z03zy">
                        <span className="flex items-center gap-1" data-id="element-691" data-mp="1t1durij6yhui"><CalendarIcon className="w-3.5 h-3.5" data-id="element-692" data-mp="1t02p1d66r00r" /> {post.date}</span>
                        <span className="flex items-center gap-1" data-id="element-693" data-mp="wbwek9ml1dek"><ClockIcon className="w-3.5 h-3.5" data-id="element-694" data-mp="wcy0j954zkb2" /> {post.readTime}</span>
                      </div>
                      <h4 className="text-xl font-bold text-[#1a2332] mb-3 group-hover:text-[#007a87] transition-colors leading-snug" data-id="element-695" data-mp="10alw8eenlh40">
                        {post.title}
                      </h4>
                      <p className="text-[#5a6a7a] text-sm leading-relaxed mb-6 flex-1" data-id="element-696" data-mp="1a4kv94jfkkrq">
                        {post.excerpt}
                      </p>
                      <div className="mt-auto flex items-center text-[#007a87] font-bold text-sm gap-1 group-hover:gap-2 transition-all" data-id="element-697" data-mp="hl5n52owkgbz">
                        Read More <ArrowRightIcon className="w-4 h-4" data-id="element-698" data-mp="12b5g7xe8pz2h" />
                      </div>
                    </div>
                  </div>
                </FadeIn>)}
            </div>
          </div>

          {/* Load More */}
          <FadeIn direction="up" data-id="element-699" data-mp="1g8u2n9nyhe9n">
            <div className="text-center mt-12" data-id="element-700" data-mp="1g82904dx5qm0">
              <button className="bg-white border-2 border-gray-200 text-[#1a2332] hover:border-[#007a87] hover:text-[#007a87] font-bold py-3 px-8 rounded-full transition-colors" data-id="element-701" data-mp="il3eo0ap8ura">
                Load More Articles
              </button>
            </div>
          </FadeIn>
        </div>
      </main>

      <StickyFooter data-id="element-702" data-mp="x9g7ioehpxhk" />
    </div>;
}